//import axios from "axios";
import { useState, useCallback } from "react";
import { useUserStore, useNotificationsStore } from "~/stores";
import { useNavigate } from "react-router";
import client from "../api";
import type { AxiosRequestConfig, AxiosError, AxiosResponse } from "axios";

// constants
import { NOTIFICATIONS } from "~/constants/notifications";
// Stores
import { NotificationSeverity } from "~/types/notifications";
import { isArrayWithContent } from "~/utils/array";

interface UseAxiosOptions {
    successHandler?: string;
    errorHandler?: string;
    noSnackbarStatuses?: number[];
    noSnackbar?: boolean;
}

/**
 * Use Axios hook
 */
export const useAxios = <T = any,>(
    params: AxiosRequestConfig = {},
    defaultOptions: UseAxiosOptions = {},
    reset?: boolean,
) => {
    const [state, setState] = useState<{
        response?: AxiosResponse<T>;
        error?: AxiosError;
        loading: boolean;
    }>({ loading: false });
    const navigate = useNavigate();

    const forceReset = () => {
        setState(prev => ({ ...prev, response: undefined, error: undefined }));
    };

    const { team } = useUserStore();

    /**
     * update notifications atom with errors
     */
    const { setNotifications } = useNotificationsStore();
    // In-flight requests map (module-scoped singleton)
    // Keyed by method|url|sortedParams|sortedData|teamId to dedupe identical concurrent calls.
    // Stored outside hook body so all hook invocations share it.
    // We attach it to globalThis to keep single instance across HMR in dev.
    const inflight: Map<string, Promise<AxiosResponse<any>>> = (
        globalThis as any
    ).__SCP_INFLIGHT__ || ((globalThis as any).__SCP_INFLIGHT__ = new Map());

    const buildKey = (
        base: AxiosRequestConfig,
        override: Partial<AxiosRequestConfig>,
        teamId?: any,
    ) => {
        const merged = { ...base, ...override } as AxiosRequestConfig;
        const { method = "GET", url = "", params, data } = merged;
        // Stable JSON stringify (order keys) for params & data
        const stable = (obj: any): string => {
            if (!obj || typeof obj !== "object") return String(obj ?? "");
            if (Array.isArray(obj)) return `[${obj.map(stable).join(",")}]`;
            return `{${Object.keys(obj)
                .sort()
                .map((k: string) => `${k}:${stable((obj as any)[k])}`)
                .join(",")}}`;
        };
        return [method.toUpperCase(), url, stable(params), stable(data), teamId]
            .filter(Boolean)
            .join("|");
    };

    const fetchData = useCallback(
        async (
            paramsOverride: Partial<AxiosRequestConfig> = {},
            optionsOverride: Partial<UseAxiosOptions> = {},
        ) => {
            // Reset errors
            setState(prev =>
                prev.error ? { ...prev, error: undefined } : prev,
            );
            const options = { ...defaultOptions, ...optionsOverride };

            try {
                setState(prev =>
                    prev.loading ? prev : { ...prev, loading: true },
                );
                const key = buildKey(params, paramsOverride, team?.teamId);
                let promise = inflight.get(key) as
                    | Promise<AxiosResponse<T>>
                    | undefined;
                if (!promise) {
                    promise = client.request<T>({
                        headers: { "X-Team-Id": team?.teamId },
                        ...params,
                        ...paramsOverride,
                    });
                    inflight.set(key, promise);
                    // Cleanup once settled
                    promise.finally(() => {
                        // Only delete if the same promise is still stored
                        const current = inflight.get(key);
                        if (current === promise) inflight.delete(key);
                    });
                }
                const result = await promise;
                setState(prev =>
                    prev.response === result
                        ? prev
                        : { ...prev, response: result },
                );
                if (options?.successHandler) {
                    setNotifications({
                        severity: NOTIFICATIONS.SEVERITY
                            .SUCCESS as NotificationSeverity,
                        message: `${options.successHandler}`,
                        autoHide: true,
                    });
                }
                if (reset)
                    setState(prev =>
                        prev.error ? { ...prev, error: undefined } : prev,
                    );
                return result;
            } catch (error: any) {
                // Capture 401 requests, assume unauthorized user. Force a document request so
                // the server can do a silent SSO session refresh. Try to land on the same page
                // as where the user currently is.

                if (error.status === 401) {
                    window.location.href = window.location.pathname;
                    return; // Nothing after this really matters.
                }

                // Handle API 401 status
                // if the user is not assigned to any team then we need to show 401 page
                if (
                    error?.response?.status === 401 &&
                    error?.response?.data?.source === "SCP"
                ) {
                    navigate("/401");
                    return;
                }

                setState(prev =>
                    prev.error === error ? prev : { ...prev, error },
                );

                if (error.response?.data || error.message) {
                    // In some api's we handle the error inside an input field
                    // for instance: show the uniqueness of a certain value, we need to hide the snackbar error in that case
                    if (
                        !options?.noSnackbarStatuses ||
                        !isArrayWithContent(options?.noSnackbarStatuses) ||
                        !error?.response?.status ||
                        !options?.noSnackbarStatuses?.includes(
                            error?.response?.status,
                        )
                    ) {
                        setNotifications({
                            severity: NOTIFICATIONS.SEVERITY
                                .ERROR as NotificationSeverity,
                            message: options.errorHandler
                                ? `${options.errorHandler}, ${
                                      error.response?.data?.message ||
                                      error?.message
                                  }`
                                : error.response?.data?.message ||
                                  error?.message,
                            autoHide: true,
                        });
                    }
                }
                if (reset)
                    setState(prev =>
                        prev.response ? { ...prev, response: undefined } : prev,
                    );
                return error;
            } finally {
                setState(prev =>
                    prev.loading ? { ...prev, loading: false } : prev,
                );
            }
        },
        [
            defaultOptions,
            params,
            reset,
            team?.teamId,
            navigate,
            setNotifications,
        ],
    );

    return {
        response: state.response,
        error: state.error,
        loading: state.loading,
        fetch: fetchData,
        forceReset,
    };
};
